/**
 * v3 combined allergies/dislikes API.
 * GET/POST `allergy_dislikes`; optional flat metadata from `allergy_dislike_categories`.
 * v1/v2 continue to use preferences domain API (`allergies` / `dislikes`).
 */
import { apiGet, getApiClient } from '../../../shared/api/client';
import { endpoints } from '../../../shared/config/endpoints';

/** GET `allergy_dislikes` — nested catalog + customer selections. */
export interface AllergyDislikesResponse {
  success?: boolean;
  code?: number;
  message?: string;
  data?: {
    /** Nested category → sub_category → items tree (catalog). */
    categories?: AllergyDislikeCategoryNode[];
    /** Selected inventory rows or ids. */
    customer_selected_items?: AllergyDislikeSelectedItem[] | number[];
  };
}

export interface AllergyDislikeCategoryNode {
  id: number;
  slug?: string;
  title?: string;
  a_title?: string;
  description?: string;
  is_active?: boolean;
  sub_categories?: AllergyDislikeSubCategoryNode[];
}

export interface AllergyDislikeSubCategoryNode {
  id: number;
  slug?: string;
  title?: string;
  a_title?: string;
  description?: string;
  is_active?: boolean;
  items?: AllergyDislikeInventoryItem[];
}

export interface AllergyDislikeInventoryItem {
  id: number;
  slug?: string;
  title?: string;
  a_title?: string;
  description?: string;
  is_active?: boolean;
  inventory_id?: number;
}

export type AllergyDislikeSelectedItem =
  | number
  | string
  | AllergyDislikeInventoryItem
  | { id?: number; inventory_id?: number };

/** GET `allergy_dislike_categories` — flat category + sub-category metadata (no items). */
export interface AllergyDislikeCategoriesResponse {
  success?: boolean;
  code?: number;
  message?: string;
  data?: {
    allergy_dislike_categories?: AllergyDislikeFlatCategory[];
    allergy_dislike_sub_categories?: AllergyDislikeFlatSubCategory[];
  };
}

export interface AllergyDislikeFlatCategory {
  id: number;
  slug?: string;
  title?: string;
  a_title?: string;
  description?: string;
  is_active?: boolean;
}

export interface AllergyDislikeFlatSubCategory {
  id: number;
  allergy_dislike_category_id: number;
  slug?: string;
  title?: string;
  a_title?: string;
  description?: string;
  is_active?: boolean;
}

function buildInventoryIdsFormData(inventory_ids: number[]): FormData {
  const form_data = new FormData();
  inventory_ids.forEach(id => {
    form_data.append('inventory_ids[]', String(id));
  });
  return form_data;
}

/** GET customer catalog + selections (`allergy_dislikes?customer_id=`). */
export async function fetchAllergyDislikes(customer_id: number): Promise<AllergyDislikesResponse> {
  const endpoint = endpoints.allergy_dislikes;
  const params = { customer_id };
  const res = await apiGet<AllergyDislikesResponse>(endpoint, params);
  return res ?? {};
}

/** GET flat category metadata (`allergy_dislike_categories?customer_id=`). Items come from allergy_dislikes GET. */
export async function fetchAllergyDislikeCategories(
  customer_id: number,
): Promise<AllergyDislikeCategoriesResponse> {
  const endpoint = endpoints.allergy_dislike_category;
  const params = { customer_id };
  const res = await apiGet<AllergyDislikeCategoriesResponse>(endpoint, params);
  return res ?? {};
}

/** POST selections as multipart `inventory_ids[]` with `customer_id` query param. */
export async function saveAllergyDislikes(
  customer_id: number,
  inventory_ids: number[],
): Promise<{ success?: boolean }> {
  const endpoint = endpoints.allergy_dislikes;
  const params = { customer_id };
  const form_data = buildInventoryIdsFormData(inventory_ids);
  const res = await getApiClient().post<{ success?: boolean }>(endpoint, form_data, { params });
  return res.data ?? { success: true };
}
